import type { SxProps, Theme } from '@mui/material';

export type QrBorderVariant =
  | 'accentLine'
  | 'accentSolid'
  | 'quietZone'
  | 'quietZoneAccent'
  | 'quietZoneFramed';

export const QR_BORDER_VARIANTS: QrBorderVariant[] = [
  'accentLine',
  'accentSolid',
  'quietZone',
  'quietZoneAccent',
  'quietZoneFramed',
];

export const ACTIVE_QR_BORDER_VARIANT: QrBorderVariant = 'quietZoneFramed';

export const QR_CENTER_MARK_BORDER = '1px solid #000000';

export function qrCenterMarkOutline(centerIsPhoto: boolean, markRadius: string | number): boolean {
  return centerIsPhoto || markRadius === '10%';
}

export const DEFAULT_QUIET_ZONE_PX = 16;

export const DEFAULT_MID_BAND_PX = 12;

export const profileQrBgColor = 'var(--qc-shell-surface, #071426)';
export const profileQrFgColor = 'var(--qc-accent, #2a9dff)';

const accentLineColor = 'var(--qc-accent-line, rgba(91, 151, 255, 0.28))';
const accentSolidColor = 'var(--qc-accent, #2a9dff)';
const shellSurfaceStrong = 'var(--qc-shell-surface-strong, #091a31)';
const quietZonePaper = '#ffffff';
const quietZoneInk = '#071426';
const quietZoneBorder = 'rgba(7, 20, 38, 0.55)';

export type QrBorderSpec = {
  variant: QrBorderVariant;
  label: string;
  borderWidthPx: number;
  borderRadiusPx: number;
  quietZonePx: number;
  borderColor: string;
  quietZoneColor: string;
  qrBackgroundColor: string;
  qrForegroundColor: string;
  midBandWidthPx?: number;
  midBandColor?: string;
};

const specs: Record<QrBorderVariant, QrBorderSpec> = {
  accentLine: {
    variant: 'accentLine',
    label: 'Accent line (1px)',
    borderWidthPx: 1,
    borderRadiusPx: 8,
    quietZonePx: DEFAULT_QUIET_ZONE_PX,
    borderColor: accentLineColor,
    quietZoneColor: profileQrBgColor,
    qrBackgroundColor: profileQrBgColor,
    qrForegroundColor: profileQrFgColor,
  },
  accentSolid: {
    variant: 'accentSolid',
    label: 'Accent solid (2px)',
    borderWidthPx: 2,
    borderRadiusPx: 8,
    quietZonePx: DEFAULT_QUIET_ZONE_PX,
    borderColor: accentSolidColor,
    quietZoneColor: profileQrBgColor,
    qrBackgroundColor: profileQrBgColor,
    qrForegroundColor: profileQrFgColor,
  },
  quietZone: {
    variant: 'quietZone',
    label: 'White quiet zone',
    borderWidthPx: 1,
    borderRadiusPx: 8,
    quietZonePx: DEFAULT_QUIET_ZONE_PX,
    borderColor: quietZoneBorder,
    quietZoneColor: quietZonePaper,
    qrBackgroundColor: quietZonePaper,
    qrForegroundColor: quietZoneInk,
  },
  quietZoneAccent: {
    variant: 'quietZoneAccent',
    label: 'B&W + accent frame',
    borderWidthPx: 2,
    borderRadiusPx: 8,
    quietZonePx: DEFAULT_QUIET_ZONE_PX,
    borderColor: accentSolidColor,
    quietZoneColor: quietZonePaper,
    qrBackgroundColor: quietZonePaper,
    qrForegroundColor: quietZoneInk,
  },
  quietZoneFramed: {
    variant: 'quietZoneFramed',
    label: 'B&W + strong band + accent',
    borderWidthPx: 2,
    borderRadiusPx: 8,
    quietZonePx: DEFAULT_QUIET_ZONE_PX,
    borderColor: accentSolidColor,
    quietZoneColor: quietZonePaper,
    qrBackgroundColor: quietZonePaper,
    qrForegroundColor: quietZoneInk,
    midBandWidthPx: DEFAULT_MID_BAND_PX,
    midBandColor: shellSurfaceStrong,
  },
};

export function getQrBorderSpec(variant: QrBorderVariant): QrBorderSpec {
  return specs[variant];
}

export function getQrQuietInnerSx(variant: QrBorderVariant): SxProps<Theme> {
  const spec = getQrBorderSpec(variant);
  return {
    display: 'inline-block',
    lineHeight: 0,
    boxSizing: 'border-box',
    padding: `${spec.quietZonePx}px`,
    borderRadius: `${Math.max(spec.borderRadiusPx - (spec.midBandWidthPx ?? 0), 0)}px`,
    backgroundColor: spec.quietZoneColor,
  };
}

export function getQrBorderSx(variant: QrBorderVariant): SxProps<Theme> {
  const spec = getQrBorderSpec(variant);
  if (variant === 'quietZoneFramed') {
    const mid = spec.midBandWidthPx ?? DEFAULT_MID_BAND_PX;
    return {
      display: 'inline-block',
      lineHeight: 0,
      boxSizing: 'border-box',
      padding: `${mid}px`,
      border: `${spec.borderWidthPx}px solid ${spec.borderColor}`,
      borderRadius: `${spec.borderRadiusPx}px`,
      backgroundColor: spec.midBandColor ?? shellSurfaceStrong,
    };
  }
  return {
    display: 'inline-block',
    lineHeight: 0,
    boxSizing: 'border-box',
    padding: `${spec.quietZonePx}px`,
    border: `${spec.borderWidthPx}px solid ${spec.borderColor}`,
    borderRadius: `${spec.borderRadiusPx}px`,
    backgroundColor: spec.quietZoneColor,
  };
}

export function isQrBorderVariant(value: string): value is QrBorderVariant {
  return QR_BORDER_VARIANTS.includes(value as QrBorderVariant);
}
